import { Progress } from 'antd';
import parser from 'html-react-parser';
import React from 'react';
import { LuArrowUpRight } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import Offeredby from '../../../../../assets/images/deals/offerBy.svg';
import triangle from '../../../../../assets/images/deals/triangle.svg';
import useIntervalTransition from '../../../../../hooks/useIntervalTransition';
import { dealStatus, dealTypes } from '../../../../login/utils/Status';
import { commaSeperator } from '../../../../utils/validation';
import { Deals } from '../../../deals/redux/types';
import { ProgressBarPercent, createInterestRate, createMinInvestment, getType, isShowFields } from '../../utils';
import DealTags from './DealTags';

type Props = {
    list: Deals[];
    isInvestment?: boolean;
};

export const titleClass = 'text-[#27AE60] font-[WorkSans] font-normal text-xs text_wrap';
export const valueClass = 'text-[#142C53] text-sm font-[WorkSans-Semibold] font-semibold';
export const investmentValueClass = 'text-[white] text-sm font-[WorkSans-Semibold] font-semibold';
interface InfoBlockProps {
    title: string;
    value?: any;
    isInvestment?: boolean;
}
const InfoBlock: React.FC<InfoBlockProps> = ({ title, value = '', isInvestment }) => (
    <div>
        <p className={titleClass}>{title}</p>
        <p className={isInvestment ? investmentValueClass : valueClass}>{value}</p>
    </div>
);

const DealsGridView: React.FC<Props> = ({ list, isInvestment }) => {
    const navigate = useNavigate();
    const currentIndex = useIntervalTransition(list.length, 300);

    //Redirect to deal detail (only Active deals)
    const redirectToDealDetails = (uuid: string, status: string) => {
        if (status === dealStatus.active) {
            navigate(`/deal/${uuid}`);
        }
    };

    return (
        <div className={isInvestment ? 'bg-[#142C53]' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'}>
            {isInvestment ? (
                <p className="text-xl text-white WorkSans-Semibold px-6 py-2 uppercase">Upcoming Deals</p>
            ) : null}
            {list.map((d, index) => {
                const isStatus = d.deal.status === dealStatus.upcoming || d.deal.status === dealStatus.completed;
                const countryColor = isInvestment ? 'text-white' : 'text-[#6D707B]';
                const cardTitle = isInvestment ? 'text-white' : 'text-[#142C53]';
                const cardDisc = isInvestment ? 'text-white' : 'text-[#020F50]';
                const showArrowButton = isInvestment ? isInvestment : dealStatus.active === d.deal.status;
                const summary = d?.deal?.info?.summary;
                const showFields = d?.deal.status && isShowFields(d?.deal.status);
                return (
                    <div
                        key={`${d.deal.uuid}`}
                        onClick={() => !isStatus && redirectToDealDetails(d.deal.uuid, d.deal.status)}
                        style={{
                            opacity: index <= currentIndex ? 1 : 0,
                            transform: `scale(${index <= currentIndex ? 1 : 0.8})`,
                            transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
                        }}
                        className={`${
                            !isStatus
                                ? 'border border-[#F1F7FF]  relative bg-[#F1F7FF] hover:shadow-card cursor-pointer'
                                : !isInvestment && 'border border-[#F1F7FF]  relative bg-[#F1F7FF]'
                        }`}
                    >
                        {/** Image and deal type  */}
                        <div className="p-2">
                            <div
                                className={`h-[70px] bg-cover bg-no-repeat bg-[#F1F7FF] flex items-center`}
                                style={d.deal.image ? { backgroundImage: `url(${d.deal.image})` } : {}}
                            >
                                {/* <div>{d.deal.image ?  <img className={`h-[70px] bg-cover bg-no-repeat bg-[#F1F7FF] flex items-center`} src={d.deal.image} /> : ''}</div> */}
                                <div className="flex justify-start pr-4 pt-2 relative right-2">
                                    {getType(d.deal.info.summary?.dealType) !== '' && (
                                        <label className="bg-white w-auto px-2 py-1 text-[#142C53] font-[WorkSans-Semibold] text-xs">
                                            {getType(d.deal.info.summary?.dealType)}
                                        </label>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/** End Image and deal type  */}

                        {/** Detail container */}
                        <div
                            className={`px-6 py-2 pb-0 h-[calc(100%-125px)] ${
                                isInvestment ? 'bg-[#142C53]' : 'bg-[#F1F7FF]'
                            }`}
                        >
                            <div className={'flex flex-col justify-between h-full'}>
                                <div className={isInvestment ? '' : 'min-h-[18rem]'}>
                                    {/** Category and Country */}
                                    <p className="text-xs font-[WorkSans] lg:font-normal  min-h-[1rem]">
                                        <span className={countryColor}>{d.issuer?.category}</span>
                                        {d.issuer?.country && (
                                            <span className={countryColor}> | {d.issuer.country}</span>
                                        )}
                                    </p>
                                    {/** End Category and Country */}

                                    {/** Deal Name */}
                                    <p
                                        className={`${cardTitle} uppercase whitespace-nowrap block text-ellipsis overflow-hidden font-semibold font-[WorkSans-Semibold] text-2xl my-1`}
                                    >
                                        {d.deal.info.summary?.title}
                                    </p>
                                    {/**End Deal Name */}

                                    {/** Deal Description */}
                                    <p className={`${cardDisc} font-[Lato] text-xs line_truc min-h-[3rem] mb-2`}>
                                        {d.deal.info.summary?.description}
                                    </p>
                                    {/** End Deal Description */}

                                    <div className="h-2" />
                                    {/** Info */}
                                    <div className="grid grid-cols-2 gap-2">
                                        {/** Target Funding Size */}
                                        <InfoBlock
                                            isInvestment={isInvestment}
                                            title={'Target Funding Size'}
                                            value={
                                                summary?.targetFundingSize
                                                    ? `${summary.targetFundingSize.currency} ${commaSeperator(
                                                          String(summary.targetFundingSize.amountUpto2dp || 0),
                                                      )}`
                                                    : ''
                                            }
                                        />

                                        {/** Asset Class Or Instrument Type */}
                                        <InfoBlock
                                            isInvestment={isInvestment}
                                            title={
                                                dealTypes.fund === summary?.dealType ? 'Asset Class' : 'Instrument Type'
                                            }
                                            value={summary?.instrumentType}
                                        />

                                        {/**Fund Manager */}
                                        {dealTypes.fund === summary?.dealType &&
                                            (showFields || dealStatus.upcoming === d.deal.status) && (
                                                <InfoBlock
                                                    isInvestment={isInvestment}
                                                    title={'Fund Manager'}
                                                    value={d.issuer?.name}
                                                />
                                            )}

                                        {/**Tenor */}
                                        {dealTypes.debt === summary?.dealType &&
                                            (showFields || dealStatus.upcoming === d.deal.status) && (
                                                <InfoBlock
                                                    isInvestment={isInvestment}
                                                    title={'Tenor'}
                                                    value={
                                                        summary.terms?.tenorInMonths
                                                            ? `${summary.terms?.tenorInMonths} months`
                                                            : ''
                                                    }
                                                />
                                            )}
                                        {/**Distribution */}
                                        {(dealTypes.debt === summary?.dealType ||
                                            dealTypes.fund === summary?.dealType) &&
                                            showFields && (
                                                <InfoBlock
                                                    isInvestment={isInvestment}
                                                    title={'Distribution'}
                                                    value={summary.terms?.distribution}
                                                />
                                            )}
                                        {/**Equity Funding To Date */}
                                        {dealTypes.equity === summary?.dealType && showFields && (
                                            <InfoBlock
                                                isInvestment={isInvestment}
                                                title={'Equity Funding To Date'}
                                                value={
                                                    summary.terms?.equityFundingToDate?.amountUpto2dp
                                                        ? `${
                                                              summary.terms.equityFundingToDate.currency
                                                          } ${commaSeperator(
                                                              String(summary.terms.equityFundingToDate.amountUpto2dp),
                                                          )}`
                                                        : ''
                                                }
                                            />
                                        )}
                                        {/**Funding Round */}
                                        {dealTypes.equity === summary?.dealType && showFields && (
                                            <InfoBlock
                                                isInvestment={isInvestment}
                                                title={'Funding Round'}
                                                value={summary.terms?.fundingRound}
                                            />
                                        )}
                                        {/** Target Return (p.a.) Or Interest Rate (p.a.)*/}
                                        {(dealTypes.debt === summary?.dealType ||
                                            dealTypes.fund === summary?.dealType) &&
                                            (showFields || d.deal.status === dealStatus.upcoming) && (
                                                <InfoBlock
                                                    isInvestment={isInvestment}
                                                    title={
                                                        dealTypes.fund === summary?.dealType
                                                            ? 'Target Return (p.a.)'
                                                            : 'Interest Rate (p.a.)'
                                                    }
                                                    value={parser(createInterestRate(d.deal))}
                                                />
                                            )}
                                        {/**Min Investment */}
                                        {showFields && createMinInvestment(d.deal) !== '-' && (
                                            <InfoBlock
                                                isInvestment={isInvestment}
                                                title={'Min Investment'}
                                                value={parser(createMinInvestment(d.deal))}
                                            />
                                        )}

                                        {/**Industry */}
                                        {dealTypes.equity === summary?.dealType && showFields && (
                                            <InfoBlock
                                                isInvestment={isInvestment}
                                                title={'Industry'}
                                                value={d.issuer?.category}
                                            />
                                        )}
                                    </div>
                                    {/** End Info */}
                                </div>
                                {!isInvestment && (
                                    <>
                                        {/** Offered by */}
                                        <div className="flex items-center mb-2 mt-8">
                                            <label className="text-[#6D707B] text-xs font-[WorkSans]">
                                                Offered by:
                                            </label>
                                            <img src={Offeredby} alt="offeredBy" className="relative top-[1px] ml-1" />
                                        </div>
                                        {/** End Offered by */}

                                        {/**Progress bar */}
                                        {dealStatus.upcoming !== d.deal.status &&
                                            dealTypes.fund !== summary?.dealType && (
                                                <div className="flex items-center my-2">
                                                    <span className="text-[#142C53] font-[WorkSans-Semibold] text-sm mr-2">
                                                        {isNaN(ProgressBarPercent(d.deal))
                                                            ? 0
                                                            : ProgressBarPercent(d.deal)}
                                                        %
                                                    </span>
                                                    <Progress
                                                        percent={ProgressBarPercent(d.deal)}
                                                        showInfo={false}
                                                        strokeColor={'#41D17E'}
                                                        trailColor="#fff"
                                                    />
                                                </div>
                                            )}
                                        {/** End Progress bar */}
                                        <DealTags dealsData={d.deal} limit={3} />
                                        {/** End Tags */}
                                    </>
                                )}
                            </div>
                            {showArrowButton && isInvestment && (
                                <div className="flex items-end justify-end -mr-6">
                                    <div className="relative">
                                        <img src={triangle} alt="arrow" />
                                        <LuArrowUpRight
                                            size={'1.6rem'}
                                            color="#41D17E"
                                            className="absolute right-2 top-8"
                                        />
                                    </div>
                                </div>
                            )}

                            {/** End View Deal arrow button */}
                        </div>
                        {/** End Detail container */}
                        {/** View Deal arrow button */}
                        {showArrowButton && !isInvestment && (
                            <div className="flex items-end justify-end absolute right-0 bottom-0">
                                <div className="relative">
                                    <img src={triangle} alt="arrow" />
                                    <LuArrowUpRight
                                        size={'1.6rem'}
                                        color="#41D17E"
                                        className="absolute right-2 top-8"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default DealsGridView;
